import { useState, useMemo } from "react"
import { createFileRoute, Link } from "@tanstack/react-router"
import { MapPin, PanelRightOpen, PanelRightClose, Loader2 } from "lucide-react"
import { TimeSeriesChart } from "@/components/graph/time-series-chart"
import { GraphSidebar } from "@/components/graph/graph-sidebar"
import { ChartToolbar } from "@/components/graph/chart-toolbar"
import { ContextBar } from "@/components/graph/context-bar"
import { useMapStore } from "@/stores/map-store"
import { useGraphWorkbenchData } from "@/features/workbench/hooks/use-graph-workbench-data"
import { fetchEnvironmentTypes } from "@/lib/api/environment"

export const Route = createFileRoute("/_authenticated/$siteSlug/graph")({
  loader: async ({ context: { queryClient } }) => {
    void queryClient.ensureQueryData({
      queryKey: ["environment-types"],
      queryFn: fetchEnvironmentTypes,
    })
  },
  component: GraphPage,
})

function GraphPage() {
  const { siteSlug } = Route.useParams()
  const selectedPoints = useMapStore((s) => s.getSelectedPoints(siteSlug))
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false)
  const [standardise, setStandardise] = useState(false)

  const {
    dateRange,
    setDateRange,
    dateRangeLoading,
    dateRangeEmpty,
    dateRangeError,
    readingsLoading,
    readingsFetching,
    seriesConfigs,
    rawSamplesById,
    visibleSeries,
    handleToggleSeries,
    chartAnnotations,
    chartThresholds,
    analysisLoading,
    analysisError,
    envReadingsLoading,
    envErrors,
    downsampleRate,
    setDownsampleRate,
  } = useGraphWorkbenchData({ siteSlug, selectedPoints })

  // Compute visible IDs Set for the chart — must be before any early returns (Rules of Hooks).
  // Each config may override the default-visible heuristic explicitly; otherwise
  // env series (weather/tidal) default to hidden and measurement series default to visible.
  const visibleIds = useMemo(() => {
    const ids = new Set<string>()
    for (const config of seriesConfigs) {
      const isEnv = config.scaleGroup === "weather" || config.scaleGroup === "tidal"
      const defaultVisible = config.defaultVisible ?? !isEnv
      if (visibleSeries[config.id] ?? defaultVisible) {
        ids.add(config.id)
      }
    }
    return ids
  }, [seriesConfigs, visibleSeries])

  if (selectedPoints.length === 0) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 text-[var(--color-text-muted)]">
        <MapPin className="h-8 w-8 opacity-40" />
        <p className="text-sm font-medium text-[var(--color-text-secondary)]">
          No points selected
        </p>
        <p className="text-xs">
          Select points on the{" "}
          <Link
            to="/$siteSlug/map"
            params={{ siteSlug }}
            className="text-[var(--color-accent-blue)] hover:underline"
          >
            map view
          </Link>{" "}
          to view their time series
        </p>
      </div>
    )
  }

  if (dateRangeError) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 text-[var(--color-text-muted)]">
        <p className="text-sm font-medium text-[var(--color-text-secondary)]">
          Failed to load date range
        </p>
        <p className="text-xs">Try refreshing or re-selecting points</p>
      </div>
    )
  }

  // Resolved with no usable bounds: the selection has no readings, so there is
  // no window to plot or edit. Checked before the loading branch, which would
  // otherwise spin forever waiting for a range that will never arrive.
  if (dateRangeEmpty) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 text-[var(--color-text-muted)]">
        <p className="text-sm font-medium text-[var(--color-text-secondary)]">
          No readings for the selected points
        </p>
        <p className="text-xs">
          Try a different selection on the{" "}
          <Link
            to="/$siteSlug/map"
            params={{ siteSlug }}
            className="text-[var(--color-accent-blue)] hover:underline"
          >
            map view
          </Link>
        </p>
      </div>
    )
  }

  if (dateRangeLoading || !dateRange || readingsLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-[var(--color-border-subtle)] border-t-[var(--color-accent-blue)]" />
      </div>
    )
  }

  const sidebarProps = {
    series: seriesConfigs,
    rawSamplesById,
    visibleSeries,
    onToggleSeries: handleToggleSeries,
    siteSlug,
    dateRange,
    selectedPoints,
    analysisLoading,
    analysisError,
    envReadingsLoading,
    envErrors,
    readingsLoading,
  }

  return (
    <div className="flex h-full flex-col">
      <ContextBar
        siteSlug={siteSlug}
        dateRange={dateRange}
        onDateRangeChange={setDateRange}
      />
      <div className="relative flex flex-1 flex-col md:flex-row overflow-hidden">
        <div className="flex flex-1 flex-col overflow-auto p-4">
          <div className="relative">
            <ChartToolbar
              standardise={standardise}
              onStandardiseChange={setStandardise}
              downsampleRate={downsampleRate}
              onDownsampleChange={setDownsampleRate}
            />
            <TimeSeriesChart
              series={seriesConfigs}
              height={500}
              standardise={standardise}
              visibleIds={visibleIds}
              annotations={chartAnnotations}
              thresholds={chartThresholds}
              visibleRange={dateRange}
              siteSlug={siteSlug}
            />
            {readingsFetching && (
              <div className="pointer-events-none absolute right-3 top-3 z-20 flex items-center gap-1.5 rounded-[var(--radius-card)] bg-[var(--color-bg-secondary)]/90 px-2 py-1 text-[10px] text-[var(--color-text-secondary)] backdrop-blur-sm">
                <Loader2 className="h-3 w-3 animate-spin" />
                <span>Updating…</span>
              </div>
            )}
          </div>

          {/* Mobile toggle for graph sidebar */}
          <button
            onClick={() => setMobileSidebarOpen((o) => !o)}
            className="mt-3 flex md:hidden items-center justify-center gap-2 rounded-[var(--radius-card)] border border-[var(--color-border-subtle)] bg-[var(--color-bg-secondary)] px-4 py-2 text-xs text-[var(--color-text-secondary)] transition-colors hover:bg-[var(--color-bg-tertiary)] hover:text-[var(--color-text-primary)]"
          >
            {mobileSidebarOpen ? (
              <PanelRightClose className="h-3.5 w-3.5" />
            ) : (
              <PanelRightOpen className="h-3.5 w-3.5" />
            )}
            {mobileSidebarOpen ? "Hide" : "Show"} Controls
          </button>
        </div>

        {/* Desktop sidebar */}
        <div className="hidden md:block">
          <GraphSidebar {...sidebarProps} />
        </div>

        {/* Mobile collapsible panel */}
        {mobileSidebarOpen && (
          <div className="md:hidden border-t border-[var(--color-border-subtle)]">
            <GraphSidebar {...sidebarProps} />
          </div>
        )}
      </div>
    </div>
  )
}
